import {Suspense} from 'react';
import {useNavigate} from 'react-router';
import {PermissionTypeKr} from '@di-shared/api/di/type';
import {emitEventLog} from '@di-shared/features/analytics';
import CapitalEventBannerType1 from '@di-shared/features/capital/CapitalEventBannerType1';
import CapitalFrame from '@di-shared/features/capital/CapitalFrame';
import {buildCapitalEventUrl, getHmcType} from '@di-shared/features/capital/capitalMessage';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {useEnvironmentStore} from '@di-shared/features/environment/environmentStore';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import {TabType} from '@di-shared/features/home/kr/HomeContents.kr';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import {isEventDuration} from '@di-shared/pages/[vin]/capital/event1';
import {ROUTE_PATHS} from '@di-shared/routes';
import {ThemeType} from '@shared/cci/features/const/environment';
import {WebviewUtils} from '@shared/webviewUtils';

/**
 * 현대캐피탈은 운영계가 2개의 계로 분리되어있음
 * 운영베타 -> 테스트용
 * 운영 -> 실제 운영
 * 따라서 isProduction 옵션에 따라 랜딩되는 페이지를 분리하고, 베타는 화이트리스트만 접근 가능하게끔 함
 * (TEST) 10/23 이벤트 페이지 연결 -> mydata-app.hyundaicapital.com/mydata/main/index.html#/event/myasset-campaign-third?hmcType=${브랜드구분}&cbno=${차대번호}
 *
 * (TEST) 10/23 이벤트 페이지 연결_beta -> preprod-mydata-app.hyundaicapital.com/mydata/main/index.html#/event/myasset-campaign-third?hmcType=${브랜드구분}&cbno=${차대번호}
 *
 * (TEST) 10/23 이벤트 페이지 연결_beta_R00004 -> preprod-mydata-app.hyundaicapital.com/mydata/main/R00004/index.html#/event/myasset-campaign-third?hmcType=${브랜드구분}&cbno=${차대번호}
 *
 */
const HCS_HOME = 'https://mydata-app.hyundaicapital.com/mydata/main/index.html#/myassets';

export interface CapitalContentsProps {
  vin: string;
  isProduction: boolean;
  /**
   * 독립 홈(/capital/short)용 마크업. 네비게이션에 타이틀이 있으므로 로고만 가운데 정렬하고 car_maintenance 섹션 안에 배너를 둠
   */
  isLogoCenter?: boolean;
}

function CapitalContents(props: CapitalContentsProps): JSX.Element {
  const {getExternalVariable} = useFirebaseStore(({getExternalVariable}) => ({getExternalVariable}));
  const betaEventUrl = buildCapitalEventUrl(getExternalVariable('CAPITAL_EVENT_URL_BETA'), props.vin);
  const eventUrl = buildCapitalEventUrl(getExternalVariable('CAPITAL_EVENT_URL'), props.vin);

  return (
    <Suspense>
      {/*// [2025-07-21:cy.seo] FIXME: 임시 이벤트 연결 -> 삭제할것 EVENT2*/}
      {!props.isProduction && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            margin: '12px 20px',
            padding: '12px',
            border: '1px dashed var(--outline-low)',
            borderRadius: 'var(--radius-medium)',
            background: 'var(--surface-container)',
            color: 'var(--on-surface)',
          }}
        >
          <button
            type="button"
            style={{
              padding: '10px',
              border: '1px solid var(--outline-low)',
              borderRadius: 'var(--radius-small)',
              background: 'var(--surface)',
              color: 'var(--on-surface)',
              textAlign: 'left',
              fontFamily: 'inherit',
              fontSize: 'var(--font-size-body-medium)',
              lineHeight: 'var(--font-line-height-body-medium)',
              fontWeight: 600,
              wordBreak: 'break-all',
            }}
            onClick={() => {
              WebviewUtils.openNewWebView({
                url: betaEventUrl,
                isModal: true,
                isNavigationBar: false,
                isToolBar: false,
              });
            }}
          >
            (TEST) {betaEventUrl}
          </button>
          <button
            type="button"
            style={{
              padding: '10px',
              border: '1px solid var(--outline-low)',
              borderRadius: 'var(--radius-small)',
              background: 'var(--surface)',
              color: 'var(--on-surface)',
              textAlign: 'left',
              fontFamily: 'inherit',
              fontSize: 'var(--font-size-body-medium)',
              lineHeight: 'var(--font-line-height-body-medium)',
              fontWeight: 600,
              wordBreak: 'break-all',
            }}
            onClick={() => {
              WebviewUtils.openNewWebView({
                url: eventUrl,
                isModal: true,
                isNavigationBar: false,
                isToolBar: false,
              });
            }}
          >
            (TEST) {eventUrl}
          </button>
        </div>
      )}
      <Inner {...props} />
    </Suspense>
  );
}

function Inner({vin, isProduction, isLogoCenter}: CapitalContentsProps) {
  const {getFeatureAgreed} = useAvailableComponentKr({vin});

  const isCapitalAllAgreed =
    getFeatureAgreed(PermissionTypeKr.FINANCIAL_MANAGEMENT) && getFeatureAgreed(PermissionTypeKr.FINANCIAL_MANAGEMENT_3RD);

  return isCapitalAllAgreed ? (
    <Contents
      vin={vin}
      isProduction={isProduction}
      isLogoCenter={isLogoCenter}
    />
  ) : (
    <Onboarding vin={vin} />
  );
}

function Contents({vin, isProduction, isLogoCenter}: CapitalContentsProps) {
  const {t} = useInferableTranslation();
  const {checkCapitalVinWhitelist, getExternalVariable} = useFirebaseStore(({checkCapitalVinWhitelist, getExternalVariable}) => ({
    checkCapitalVinWhitelist,
    getExternalVariable,
  }));
  const theme = useEnvironmentStore((state) => state.theme);
  const to = `${isProduction ? HCS_HOME : getExternalVariable('CAPITAL_MYASSETS_URL_BETA')}?isLightMode=${theme === ThemeType.LIGHT ? 'Y' : 'N'}&hmcType=${getHmcType()}`;

  const logo = (
    <ImageSelector
      alt={`현대캐피탈 logo`}
      kiaLightSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo_light.png')}
      kiaDarkSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo.png')}
      hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo_light.png')}
      hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo.png')}
      genesisLightSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo_light.png')}
      genesisDarkSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo.png')}
      className="logo"
    />
  );

  // [2025-09-04:cy.seo] FIXME: 이벤트 후 삭제 필요 EVENT2
  const eventBanner = (checkCapitalVinWhitelist(vin) || isEventDuration()) && (
    <CapitalEventBannerType1
      key="finance"
      vin={vin}
      onClick={() => {
        emitEventLog({
          event: 'di_vehicle_finance',
          data_action: 'view banner',
          data_name: '똑똑한 내 차 관리 시원한 커피와 함께',
        });
      }}
    />
  );

  // fixme 공공서비스 작업으로 인한 CSS 깨짐으로 인하여 마크업 구조 임시 대응
  // 3.2.5.V-개발-DI-Platform/capital css 이슈 수정
  const frame = (
    <div style={{overflowX: 'hidden'}}>
      <CapitalFrame
        vin={vin}
        to={to}
        isInitMounted={false}
      />
    </div>
  );

  if (isLogoCenter) {
    return (
      <>
        <section className="section car_maintenance">
          <h2 className="blind">{t('T40154')}</h2>
          <div className="section_container_header logo_center">
            <div className="logo_area">{logo}</div>
          </div>
          {eventBanner}
        </section>
        {frame}
      </>
    );
  }

  return (
    <>
      <section
        className="section section_service_connect"
        id={`${TabType.Finance}_tabpanel`}
        aria-labelledby={`${TabType.Finance}_tab`}
      >
        <h2 className="blind">{t('T40154')}</h2>
        <div className="section_container_header">
          <h3 className="title_xsmall_b">
            {t('T40155')}
            {!isProduction && ' (beta)'}
          </h3>
          <div className="logo_area">{logo}</div>
        </div>
      </section>
      {eventBanner}
      {frame}
    </>
  );
}

function Onboarding({vin}: Omit<CapitalContentsProps, 'isProduction'>): JSX.Element {
  const {t} = useInferableTranslation();
  const theme = useEnvironmentStore((state) => state.theme);
  const navigate = useNavigate();

  return (
    <section className="section section_service_connect">
      <div className="section_container_header">
        <h3 className="title_xsmall_b">{t('T40155')}</h3>
        <div className="logo_area">
          <ImageSelector
            id={`fe--capital-short-logo`}
            alt={`현대캐피탈 logo`}
            kiaLightSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo_light.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo_light.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo_light.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo.png')}
            width="106"
            height="20"
            className="logo"
          />
        </div>
      </div>
      <div className="section_container">
        <div className="section_heading">
          <span
            className="section_heading_title"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T40165')}
          </span>
          <p className="section_heading_desc">{t('T40164')}</p>
        </div>
        <div className="img_box">
          <ImageSelector
            alt={`현대캐피탈 logo`}
            kiaLightSrc={() => import('@di-shared/assets/img/home/img_capital_light.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/home/img_capital.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/img_capital_light.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/img_capital.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/img_capital_light.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/img_capital.png')}
            width="106"
            height="20"
            className="logo"
          />
        </div>

        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() => {
            emitEventLog({
              event: 'di_start_asset_linking',
            });
            navigate(ROUTE_PATHS.CAPITAL_TERMS.getFullPath(vin));
          }}
        >
          {t('T40151')}
        </button>
      </div>
    </section>
  );
}

export default CapitalContents;
